import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Chip, TextField } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
} from 'recharts';

type RiskA = {
  score: number;
  category: 'Low' | 'Medium' | 'High';
};
type RiskB = {
  time: number;
  cost: number;
  severity: 'Low' | 'Moderate' | 'High';
};

type NavState = {
  delaysCount?: number;
  bottlenecksCount?: number;
  risk?: RiskA | RiskB;
  // Extended state from AIDetails for a richer dashboard
  delays?: string[];
  bottlenecks?: string[];
  optimistic?: string[];
  realistic?: string[];
  accelerated?: string[];
  timeSummary?: string[];
  costSummary?: string[];
  recommendations?: string[];
  taskId?: string | null;
  dataKey?: string | null;
};

const COLORS = ['#1976d2', '#9c27b0'];

export default function AIVisualOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    delaysCount = 0,
    bottlenecksCount = 0,
    risk,
    delays = [],
    bottlenecks = [],
    optimistic = [],
    realistic = [],
    accelerated = [],
    timeSummary = [],
    costSummary = [],
    recommendations = [],
    taskId = null,
    dataKey = null,
  }: NavState = (location.state as NavState) || {};

  const [overviewNotes, setOverviewNotes] = React.useState('');
  const [scenariosNotes, setScenariosNotes] = React.useState('');
  const [actionsNotes, setActionsNotes] = React.useState('');

  const pieData = [
    { name: 'Delays', value: delaysCount },
    { name: 'Bottlenecks', value: bottlenecksCount },
  ];

  const riskScore = (() => {
    if (!risk) return 0;
    if ('score' in risk) return risk.score;
    if ('time' in risk && 'cost' in risk) return Math.round((risk.time + risk.cost) / 2);
    return 0;
  })();
  // Risk gauge data (donut): risk vs remainder
  const gaugeData = [
    { name: 'Risk', value: Math.max(0, Math.min(100, Math.round(riskScore))) },
    { name: 'Remaining', value: Math.max(0, 100 - Math.max(0, Math.min(100, Math.round(riskScore)))) },
  ];

  return (
    <Box className="print-page" sx={{ p: 2 }}>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; }
          .card { break-inside: avoid; page-break-inside: avoid; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Header and actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h5">AI Visual Overview</Typography>
          {taskId ? <Chip label={`task_id: ${taskId}`} /> : null}
          {dataKey ? <Chip label={`data_key: ${dataKey}`} /> : null}
          <Chip color="primary" variant="outlined" label={`Delays: ${delaysCount}`} />
          <Chip color="secondary" variant="outlined" label={`Bottlenecks: ${bottlenecksCount}`} />
        </Box>
        <Box className="no-print">
          <Button variant="outlined" sx={{ mr: 1 }} onClick={() => navigate(-1)}>Back</Button>
          <Button variant="outlined" sx={{ mr: 1 }} onClick={() => window.print()}>Print</Button>
          <Button variant="contained" onClick={() => navigate('/project-planning')}>Dashboard</Button>
        </Box>
      </Box>

      {/* Top visuals: Issue Mix and Risk Gauge */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card className="card">
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Issue Mix</Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
              <Typography variant="body2">Delays: {delaysCount}</Typography>
              <Typography variant="body2">Bottlenecks: {bottlenecksCount}</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Risk Gauge</Typography>
            <Box sx={{ width: '100%', height: 280, position: 'relative' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={gaugeData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={100}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <Cell key="risk" fill="#ef6c00" />
                    <Cell key="remaining" fill="#e0e0e0" />
                  </Pie>
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Typography variant="h6" align="center">{Math.round(riskScore)}%</Typography>
                {risk && 'severity' in risk ? (
                  <Typography variant="caption" align="center">{risk.severity}</Typography>
                ) : null}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Delays and Bottlenecks lists */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <Card className="card">
          <CardContent>
            <Typography variant="subtitle1">Cross-WBS Delays</Typography>
            <Box sx={{ mt: 1 }}>
              {delays.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No delays listed.</Typography>
              ) : (
                delays.map((d, i) => (
                  <Typography key={i} variant="body2">• {d}</Typography>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
        <Card className="card">
          <CardContent>
            <Typography variant="subtitle1">Global Bottlenecks</Typography>
            <Box sx={{ mt: 1 }}>
              {bottlenecks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No bottlenecks listed.</Typography>
              ) : (
                bottlenecks.map((b, i) => (
                  <Typography key={i} variant="body2">• {b}</Typography>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Scenarios */}
      <Card className="card" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1">Predictive Scenarios</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2">Optimistic</Typography>
              {optimistic.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No optimistic scenario.</Typography>
              ) : optimistic.map((o, i) => (
                <Typography key={i} variant="body2">• {o}</Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle2">Realistic</Typography>
              {realistic.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No realistic scenario.</Typography>
              ) : realistic.map((r, i) => (
                <Typography key={i} variant="body2">• {r}</Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle2">Accelerated</Typography>
              {accelerated.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No accelerated scenario.</Typography>
              ) : accelerated.map((a, i) => (
                <Typography key={i} variant="body2">• {a}</Typography>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Trade-offs */}
      <Card className="card" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1">Time vs. Cost Trade-off</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2">Time</Typography>
              {timeSummary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No time trade-off summary.</Typography>
              ) : timeSummary.map((t, i) => (
                <Typography key={i} variant="body2">• {t}</Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle2">Cost</Typography>
              {costSummary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No cost trade-off summary.</Typography>
              ) : costSummary.map((c, i) => (
                <Typography key={i} variant="body2">• {c}</Typography>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Recommended Actions */}
      <Card className="card" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1">Recommended Actions</Typography>
          <Box sx={{ mt: 1 }}>
            {recommendations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recommendations provided.</Typography>
            ) : recommendations.map((rec, i) => (
              <Typography key={i} variant="body2">{i + 1}. {rec}</Typography>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Notes sections for printing */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <Card className="card">
          <CardContent>
            <Typography variant="subtitle1">Notes: Overview</Typography>
            <TextField
              value={overviewNotes}
              onChange={(e) => setOverviewNotes(e.target.value)}
              placeholder="Add notes about issues and risk…"
              multiline
              minRows={4}
              fullWidth
            />
          </CardContent>
        </Card>
        <Card className="card">
          <CardContent>
            <Typography variant="subtitle1">Notes: Scenarios</Typography>
            <TextField
              value={scenariosNotes}
              onChange={(e) => setScenariosNotes(e.target.value)}
              placeholder="Add notes about scenarios and trade-offs…"
              multiline
              minRows={4}
              fullWidth
            />
          </CardContent>
        </Card>
      </Box>
      <Card className="card" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1">Notes: Actions</Typography>
          <TextField
            value={actionsNotes}
            onChange={(e) => setActionsNotes(e.target.value)}
            placeholder="Add notes about recommended actions…"
            multiline
            minRows={4}
            fullWidth
          />
        </CardContent>
      </Card>
    </Box>
  );
}